import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactEcharts from 'echarts-for-react';
import 'echarts-wordcloud';
import { useDispatch, useSelector } from 'react-redux';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import {
  checkUidPresenceInCheckedMeasureForTooltip,
  limitChartData,
} from '../CommonFunctions/CommonFunctions';

import EmptyQuery from './EmptyQuery';
import { RootState } from '../../redux';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import { tooltipStyle } from '../Common/EChartTooltip';
import { CombinedTheme } from '../ChartOptions/Color/ColorScheme';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import { formatChartXAxisValue } from '../ChartOptions/Format/NumberFormatter';
interface WordCloudProps {
  colorScheme?: string;
  softUI?: boolean;
  fontFamily?: string;
  propKey: string;
  graphDimension?: any;
  chartArea?: any;
  graphTileSize?: number;
}

const WordCloud = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
}: WordCloudProps) => {
  const {
    tabState,
    selectedTab,
    showDash,
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    chartControls,
    chartProperties,
  } = useSelector((state: RootState) => ({
    tabState: state.tabState,
    selectedTab: state.tabTileProps.selectedTabId,
    showDash: state.tabTileProps.showDash,
    chartProperties: state.chartProperties,
    chartControls: state.chartControls,
    tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
  }));

  const chartControl: ChartControlsProps = chartControls.properties[propKey];
  const {
    shape: userShape = 'square',
    rotationMode: userRotationMode = 'horizontal',
    gridSize: userGridSize = 8,
    minFontSize: userMinFont = 12,
    maxFontSize: userMaxFont = 40,
    drawOutOfBound: userDrawOutOfBound = false,
  } = chartControl.wordCloudOptions ?? {};

  let chartData: any[] =
    chartControl.chartData && chartControl.chartData.length > 0 ? chartControl.chartData : [];

  const [chartDataKeys, setChartDataKeys] = useState<string[]>([]);
  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  const modifiedChartData: any[] = limitChartData(
    chartData,
    100,
    chartAxes[2]?.fields[0]?.displayname,
  );

  let objKey = chartProperties.properties[propKey]?.chartAxes[1]?.fields[0]?.displayname;
  let measureKeyDisplay = chartProperties.properties[propKey]?.chartAxes[2]?.fields[0]?.displayname;
  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  useEffect(() => {
    if (chartData.length >= 1) {
      if (typeof chartData === 'object' && chartData.length > 0) {
        setChartDataKeys(Object.keys(chartData[0]));
      }
      if (objKey) {
        const mappedChartData = chartControl.chartData.map((el: any) => {
          const newEl = { ...el };
          if (objKey in newEl && typeof newEl[objKey] === 'number' && !isNaN(newEl[objKey])) {
            newEl[objKey] = newEl[objKey].toString();
          }
          if (newEl[objKey] === null || newEl[objKey] === 'null') {
            newEl[objKey] = '(Blank)';
          } else if (typeof newEl[objKey] === 'boolean') {
            newEl[objKey] = newEl[objKey] ? 'True' : 'False';
          }
          return newEl;
        });

        if (JSON.stringify(chartControl.chartData) !== JSON.stringify(mappedChartData)) {
          chartControl.chartData = mappedChartData;
        }
      }
    }
  }, [chartData, chartControl]);

  useEffect(() => {
    if (chartData.length > 100 && !showDash) {
      toast.warning(`${messages.chart.excessiveData} 100 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  const [chartThemes, setChartThemes] = useState<CombinedTheme[]>([]);
  const combinedThemes = useCombinedColorSchemes();

  useEffect(() => {
    const selectedName =
      !chartControl.colorScheme || chartArea === 'dashboard'
        ? colorScheme
        : chartControl.colorScheme;

    const selectedTheme = combinedThemes.find((theme) => theme.name === selectedName);
    if (selectedTheme) {
      setChartThemes([selectedTheme]);
    }
  }, [chartControl.colorScheme, colorScheme, combinedThemes]);

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);
  const hoveredRef = useRef<Record<string, any> | null>(null);
  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !mouseOverInteractivity ||
        (tabState.tabs[selectedTab].hoveredPropkey &&
          tabState.tabs[selectedTab].hoveredPropkey === propKey)
      ) {
        return;
      }
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      const index = modifiedChartData.findIndex((item) =>
        tabState.tabs[selectedTab].hoveredItems?.some(
          (hovered: Record<string, any>) =>
            String(hovered[chartDataKeys[0]]) === String(item[chartDataKeys[0]]),
        ),
      );

      if (index >= 0) {
        chartInstance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: index,
        });
      } else {
        chartInstance.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
        });
      }
    }, 30);

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, modifiedChartData]);

  const handleChartReady = useCallback(
    (chartInstance: any) => {
      if (!mouseOverInteractivity) return;

      chartInstanceRef.current = chartInstance;

      chartInstance.on('mouseover', (params: any) => {
        if (params?.name && hoveredRef.current?.[chartDataKeys[0]] !== params.name) {
          if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

          hoverdTimeout.current = setTimeout(() => {
            const keyName = chartDataKeys[0] || 'Name';
            const newEntry = { [keyName]: params.name };
            hoveredRef.current = newEntry;

            setHoveredColumns((prev) => {
              const isDuplicate = prev.some((item) => item[keyName] === params.name);
              if (!isDuplicate) {
                return [...prev, newEntry];
              }
              return prev;
            });
          }, 30);
        }
      });

      chartInstance.on('mouseout', (params: any) => {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
        if (params?.name) {
          hoveredRef.current = null;
          setHoveredColumns([]);
        }
      });
    },
    [chartDataKeys],
  );

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const chartOption = useMemo(() => {
    const fieldInAxes = chartAxes
      ?.flatMap((axis: any) => axis?.fields || [])
      ?.find((f: any) => f.displayname === objKey);

    const dimensionUid = fieldInAxes?.uId;
    const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
      dimensionUid
    ] as any;
    const mappedWordCloudData = modifiedChartData.map((item) => {
      const rawVal = item[objKey];
      const formattedName =
        labelFormat?.finalDateFormat && rawVal != null
          ? formatChartXAxisValue(chartControl, rawVal, labelFormat)
          : rawVal;
      return {
        ...item,
        name: formattedName,
        value: item[measureKeyDisplay],
      };
    });

    const rawIgnore: any[] = chartControl?.wordCloudOptions?.ignoreWords || [];

    const normalizedIgnore = (Array.isArray(rawIgnore) ? rawIgnore : [])
      .map((e: any) => {
        if (typeof e === 'string')
          return { word: String(e).trim().toLowerCase(), location: 'everywhere' };
        return {
          word: String(e?.word || '')
            .trim()
            .toLowerCase(),
          location: e?.location || 'everywhere',
        };
      })
      .filter((e: any) => e.word && e.word.length > 0)
      // Deduplicate
      .reduce((acc: any[], cur: any) => {
        if (!acc.some((x) => x.word === cur.word)) acc.push(cur);
        return acc;
      }, [])
      // SORT BY LENGTH (Longest First) - Critical for removing "permissions???" before "???"
      .sort((a: any, b: any) => b.word.length - a.word.length);

    const filteredWordCloudData = mappedWordCloudData
      .map((item) => {
        let modifiedName = item?.name ? String(item.name) : '';

        // Apply ignore rules
        normalizedIgnore.forEach((entry: any) => {
          const ignoredWord = entry.word;
          const location = entry.location || 'everywhere';

          // Escape special regex characters (like ?, $, ., ,, etc.)
          const escapedWord = ignoredWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // SMART BOUNDARIES
          // Only add \b if the ignored text starts/ends with a letter/number.
          // If it is a symbol (like "," or "!!!"), DO NOT use \b.
          const startsWithWordChar = /^\w/.test(ignoredWord);
          const endsWithWordChar = /\w$/.test(ignoredWord);

          const startB = startsWithWordChar ? '\\b' : '';
          const endB = endsWithWordChar ? '\\b' : '';

          if (location === 'everywhere') {
            const regex = new RegExp(`${startB}${escapedWord}${endB}`, 'gi');
            modifiedName = modifiedName.replace(regex, '');
          } else if (location === 'start') {
            const regex = new RegExp(`^\\s*${escapedWord}${endB}`, 'i');
            modifiedName = modifiedName.replace(regex, '');
          } else if (location === 'end') {
            const regex = new RegExp(`${startB}${escapedWord}\\s*$`, 'i');
            modifiedName = modifiedName.replace(regex, '');
          } else if (location === 'middle') {
            const regex = new RegExp(`\\s+${escapedWord}\\s+`, 'gi');
            modifiedName = modifiedName.replace(regex, ' ');
          }
        });

        // Clean up extra spaces
        modifiedName = modifiedName.replace(/\s+/g, ' ').trim();
        return { ...item, name: modifiedName };
      })
      .filter((item) => item.name.length > 0);

    let rotationRange = [0, 0];
    let rotationStep = 0;

    if (userRotationMode === 'random') {
      rotationRange = [-90, 90];
      rotationStep = 45;
    } else if (userRotationMode === 'ortho') {
      rotationRange = [-90, 90];
      rotationStep = 90;
    } else {
      rotationRange = [0, 0];
      rotationStep = 0;
    }

    return {
      color: chartThemes[0]?.colors || [],
      backgroundColor: chartThemes[0]?.background || [],
      tooltip: {
        show: isTooltipEnabled,
        useHTML: true,
        trigger: 'item',
        backgroundColor: 'white',
        borderWidth: 1,
        padding: 0,
        textStyle: {
          color: '#333',
          fontSize: 16,
          fontFamily: fontFamily,
        },
        appendToBody: true,
        extraCssText:
          'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
        formatter: function (params: any) {
          const tempData = params.data;
          const tempDataClone = { ...tempData };
          delete tempDataClone.textStyle;

          const dimensionKey = objKey;
          const measureKey = chartAxes.find((axis: any) => axis.name === 'Measure').fields[0]
            ?.displayname;
          const dimensionValue = tempDataClone[dimensionKey as string];

          const _data: { [key: string]: string | number } = {};
          Object.keys(tempDataClone).forEach((key: string) => {
            if (key === 'name' || key === 'value') return;

            const resp = checkUidPresenceInCheckedMeasureForTooltip(
              chartAxes,
              key,
              tooltipFormats?.checkedMeasure,
            );
            if (resp) {
              _data[key] = tempDataClone[key];
            }
          });

          let mergedData = { ..._data };
          const extraResponses = tooltipFormats?.extraMeasureFieldsResponses;
          if (tooltipFormats && extraResponses && extraResponses.length > 0) {
            const extraFieldObj = extraResponses.find(
              (obj: any) =>
                String(obj[dimensionKey as string]) === String(dimensionValue as string),
            );
            if (extraFieldObj) {
              const { [dimensionKey as string]: _, ...restExtraFields } = extraFieldObj;
              let filteredFields = { ...mergedData, ...restExtraFields };
              const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
                chartAxes,
                measureKey,
                tooltipFormats?.checkedMeasure,
              );
              if (!isPresent) {
                delete filteredFields[measureKey];
              }
              mergedData = { ...filteredFields };
            }
          }

          if (Object.keys(mergedData).length === 0) {
            mergedData = {
              [objKey]: params.name,
              [measureKeyDisplay]: params.value,
            };
          }

          return tooltipStyle(
            params.color,
            mergedData,
            tooltipFormats,
            chartAxes,
            dimensionKey,
            undefined,
            undefined,
            fontFamily,
          );
        },
      },
      series: [
        {
          type: 'wordCloud',
          shape: userShape,
          gridSize: userGridSize,
          sizeRange: [userMinFont, userMaxFont],
          rotationRange: rotationRange,
          rotationStep: rotationStep,
          drawOutOfBound: userDrawOutOfBound,

          left: 'center',
          top: 'center',
          width: '100%',
          height: '100%',
          layoutAnimation: false,
          animation: false,
          animationDuration: 0,
          animationDurationUpdate: 0,
          textStyle: {
            fontFamily: fontFamily,
            fontWeight: 'bold',
          },
          data: filteredWordCloudData.map((item, i) => ({
            ...item,
            textStyle: {
              color: chartThemes[0]?.colors?.[i % (chartThemes[0]?.colors?.length || 1)],
            },
          })),
          rendering: 'canvas',
          emphasis: {
            focus: 'none',
            scale: false,
            textStyle: {
              shadowBlur: 10,
              shadowColor: 'transparent',
            },
          },
        },
      ],
    };
  }, [
    chartData,
    chartThemes,
    fontFamily,
    isTooltipEnabled,
    objKey,
    measureKeyDisplay,
    chartAxes,
    tooltipFormats,
    chartControl?.wordCloudOptions?.ignoreWords,
    userShape,
    userRotationMode,
    userGridSize,
    userMinFont,
    userMaxFont,
  ]);

  return chartData.length < 1 ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
    />
  ) : (
    <>
      <ReactEcharts
        ref={chartRef}
        onChartReady={handleChartReady}
        style={{
          width: graphDimension.width,
          height: graphDimension.height,
          overflow: 'hidden',
          margin: 'auto',
          border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
        }}
        option={chartOption}
      />
    </>
  );
};

export default WordCloud;
